// CadinSection.jsx
import React, { useState } from "react";
import { Table, Badge } from "flowbite-react";
import TitleStripe from "../../common/TitleStripe";
import NoOcurrence from "../../common/query/NoOcurrence";
import { formatToReal } from "../../../utils/utils";

// ====================== Helpers ======================
const formatMoneyBR = (v) => {
  if (v == null) return "—";
  return Number(v).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDateBR = (d) => d || "—";

// Verifica se um campo possui informação de fato (não vazio / não "—")
const hasValue = (v) => {
  if (v == null) return false;
  if (typeof v === "boolean") return true;
  if (typeof v === "number") return !Number.isNaN(v);
  const s = String(v).trim();
  return s !== "" && s !== "—" && s.toUpperCase() !== "NAO DIVULGADO";
};

// cor para situação
const situationColor = (situacao = "") => {
  const s = situacao.toLowerCase();
  if (s.includes("ajuizada")) return "failure";
  if (s.includes("cobranca") || s.includes("cobrança")) return "warning";
  return "info";
};

// cor para indicador ajuizado
const ajuizadoColor = (indicador) => (indicador ? "failure" : "success");
const ajuizadoLabel = (indicador) => (indicador ? "Ajuizado" : "Não ajuizado");

// ====================== Component ======================
export default function CadinQueryp({ data }) {
  // Hook sempre no topo, nunca dentro de condicional
  const [showAll, setShowAll] = useState(false);

  // Garante defaults mesmo se data vier undefined/null
  const inscricoes = data?.inscricoes ?? [];
  const qtd_dividas = data?.qtd_dividas ?? inscricoes.length;
  const total_divida = data?.total_divida ?? 0;
  const data_divida_mais_atual = data?.data_divida_mais_atual ?? "—";
  const data_divida_mais_antiga = data?.data_divida_mais_antiga ?? "—";

  const hasDebts = Number(qtd_dividas) > 0;
  const hasMany = inscricoes.length > 10;
  const visibleInscricoes =
    hasMany && !showAll ? inscricoes.slice(0, 10) : inscricoes;

  const totalFormatado = formatMoneyBR(total_divida);

  // Configuração das colunas: cada uma sabe extrair o valor bruto (para testar
  // se há informação) e renderizar a célula.
  const columns = [
    {
      key: "data_inscricao",
      header: "Data",
      headClass: "whitespace-nowrap",
      accessor: (item) => item.data_inscricao,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap text-xs text-gray-700 dark:text-gray-200">
          {formatDateBR(item.data_inscricao)}
        </Table.Cell>
      ),
    },
    {
      key: "numero_inscricao",
      header: "Nº inscrição",
      headClass: "whitespace-nowrap",
      accessor: (item) => item.numero_inscricao,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap text-xs font-mono text-gray-900 dark:text-gray-100">
          {item.numero_inscricao}
        </Table.Cell>
      ),
    },
    {
      key: "situacao",
      header: "Situação",
      headClass: "whitespace-nowrap text-center",
      accessor: (item) => item.situacao,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap">
          <Badge
            color={situationColor(item.situacao)}
            size="sm"
            className="px-2 py-0.5 text-[11px] w-fit mx-auto"
          >
            {item.situacao}
          </Badge>
        </Table.Cell>
      ),
    },
    {
      key: "tipo_devedor",
      header: "Tipo devedor",
      headClass: "whitespace-nowrap",
      accessor: (item) => item.tipo_devedor,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap text-xs text-gray-700 dark:text-gray-200">
          {item.tipo_devedor}
        </Table.Cell>
      ),
    },
    {
      key: "receita_principal",
      header: "Receita principal",
      headClass: "whitespace-nowrap",
      accessor: (item) => item.receita_principal,
      render: (item) => (
        <Table.Cell className="max-w-xs text-xs text-gray-700 dark:text-gray-200">
          <span className="line-clamp-2">{item.receita_principal}</span>
        </Table.Cell>
      ),
    },
    {
      key: "valor_consolidado",
      header: "Valor",
      headClass: "whitespace-nowrap text-right",
      accessor: (item) => item.valor_consolidado,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap text-right text-xs font-semibold text-gray-900 dark:text-gray-100">
          {formatMoneyBR(item.valor_consolidado)}
        </Table.Cell>
      ),
    },
    {
      key: "indicador_ajuizado",
      header: "Ajuizado",
      headClass: "whitespace-nowrap text-center",
      accessor: (item) => item.indicador_ajuizado,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap">
          <Badge
            color={ajuizadoColor(item.indicador_ajuizado)}
            size="sm"
            className="px-2 py-0.5 text-[11px] w-fit mx-auto"
          >
            {ajuizadoLabel(item.indicador_ajuizado)}
          </Badge>
        </Table.Cell>
      ),
    },
    {
      key: "unidade_responsavel",
      header: "Unidade",
      headClass: "whitespace-nowrap",
      accessor: (item) => item.unidade_responsavel,
      render: (item) => (
        <Table.Cell className="whitespace-nowrap text-xs text-gray-700 dark:text-gray-300">
          {item.unidade_responsavel}
        </Table.Cell>
      ),
    },
  ];

  // Mantém apenas colunas em que ao menos uma linha possui informação.
  const visibleColumns = columns.filter((col) =>
    visibleInscricoes.some((item) => hasValue(col.accessor(item))),
  );

  return (
    <section className="mt-6" id="cadin">
      {/* Cabeçalho / título */}
      <TitleStripe>CADIN — Dívida Ativa União / Receita Federal</TitleStripe>

      {hasDebts ? (
        <>
          {/* Resumo em chips */}
          <div className="flex flex-wrap gap-3 mb-5 mt-4">
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <span>Quantidade Total:</span>
              <span>{qtd_dividas}</span>
            </div>

            <div className="flex items-center gap-2 bg-red-50 border border-red-100 text-red-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <span>Valor Total:</span>
              <span>{formatToReal(total_divida)}</span>
            </div>

            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <span>Data da última:</span>
              <span>{data_divida_mais_atual}</span>
            </div>

            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-sm font-medium">
              <span>Data da primeira:</span>
              <span>{data_divida_mais_antiga}</span>
            </div>
          </div>

          {/* Fundo com glow discreto */}
          <div
            className="pointer-events-none absolute inset-0 opacity-60 dark:opacity-100"
            aria-hidden
            style={{
              background:
                "radial-gradient(700px 300px at 0% 0%, rgba(227,84,18,0.09), transparent 55%)",
            }}
          />

          <div className="relative">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                Inscrições em Dívida Ativa
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                UF predominante:{" "}
                <span className="font-semibold">
                  {inscricoes[0]?.uf || "—"}
                </span>
              </p>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200/70 bg-white/70 backdrop-blur-sm dark:border-white/10 dark:bg-black/40">
              <Table hoverable>
                <Table.Head>
                  {visibleColumns.map((col) => (
                    <Table.HeadCell key={col.key} className={col.headClass}>
                      {col.header}
                    </Table.HeadCell>
                  ))}
                </Table.Head>

                <Table.Body className="divide-y">
                  {visibleInscricoes && visibleInscricoes.length > 0 ? (
                    visibleInscricoes.map((item, idx) => (
                      <Table.Row
                        key={item.numero_inscricao || idx}
                        className="bg-white/90 dark:bg-transparent"
                      >
                        {visibleColumns.map((col) => (
                          <React.Fragment key={col.key}>
                            {col.render(item)}
                          </React.Fragment>
                        ))}
                      </Table.Row>
                    ))
                  ) : (
                    <Table.Row>
                      <Table.Cell
                        colSpan={visibleColumns.length || 1}
                        className="py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        Nenhuma inscrição CADIN encontrada para este documento.
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
            </div>

            {/* Ver mais / Ver menos */}
            {hasMany && (
              <div className="mt-2 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400">
                <span>
                  {showAll
                    ? `Exibindo todas as ${inscricoes.length} inscrições.`
                    : `Exibindo 10 de ${inscricoes.length} inscrições.`}
                </span>
                <button
                  type="button"
                  onClick={() => setShowAll((prev) => !prev)}
                  className="ml-3 text-xs font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                >
                  {showAll ? "Ver menos" : "Ver mais"}
                </button>
              </div>
            )}

            {/* Observação/rodapé */}
            <p className="mt-3 text-[11px] text-gray-500 dark:text-gray-400">
              Informações obtidas junto à Dívida Ativa da União / Receita
              Federal. A ausência de registros não elimina a necessidade de
              outras análises de risco.
            </p>
          </div>
        </>
      ) : (
        <NoOcurrence />
      )}
    </section>
  );
}
